// @ts-strict-ignore
import {
  FloatingServiceMenu,
  Footer,
  Header,
  Page,
  PageTransition,
  pickSeoProps,
} from '@components/Page'
import Pagination from '@components/Pagination'
import FeaturedItem from '@components/_news/FeaturedItem'
import NewsItem from '@components/_news/NewsItem'
import { Box, Container, Grid, OldHeading, OldText } from '@gassan-ui'
import { buildLanguageAlternateSlugItems } from '@lib/build-language-alternates'
import { getServerSideTranslations } from '@lib/get-server-side-translations'
import { Item, MultipleItemResponse, SingleItemResponse, getPageByCodename } from '@lib/kontent'
import { getNewsArticles } from '@lib/kontent/getNewsArticles'
import { NewsItem as NewsItemModel } from '@lib/kontent/models/NewsItem'
import { NewsOverviewPage as NewsOverviewPageModel } from '@lib/kontent/models/NewsOverviewPage'
import { GetStaticProps } from 'next'
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import { FC, useEffect, useState } from 'react'

type Props = SingleItemResponse<NewsOverviewPageModel>

const NewsOverviewPage: FC<Props> = ({ item, modular_content }) => {
  const [status, setStatus] = useState<'loading' | 'idle' | 'error'>('loading')
  const [page, setPage] = useState(1)
  const [articles, setArticles] = useState<null | MultipleItemResponse<NewsItemModel>>(null)
  const {
    i18n: { language },
  } = useTranslation()
  const { locales } = useRouter()
  const featuredItem =
    item.elements.featured_news_item.value[0] &&
    (modular_content[item.elements.featured_news_item.value[0]] as Item<NewsItemModel>)

  useEffect(() => {
    const fetchArticles = async () => {
      setStatus('loading')
      try {
        const articles = await getNewsArticles({
          language: language,
          limit: 8,
          page,
        })
        setArticles(articles)
        setStatus('idle')
      } catch (error) {
        setStatus('error')
      }
    }
    fetchArticles()
  }, [page]) // eslint-disable-line

  return (
    <Page
      {...pickSeoProps(item.elements)}
      languageAlternates={buildLanguageAlternateSlugItems(locales, 'newsPage')}
      hideCanonical
    >
      <Header initialVariant="absolute" />
      <FloatingServiceMenu />
      <PageTransition>
        <Container maxWidth="90.5rem">
          <Box
            pt={{ _: '1.5rem', small: '3rem', large: '5rem', xlarge: '7rem' }}
            pb={{ _: '2.5rem', large: '4rem', xlarge: '5rem' }}
            maxWidth="35rem"
            textAlign={{ _: 'center', large: 'left' }}
            m={{ _: '0 auto', large: 'initial' }}
          >
            <OldHeading variant="h1" as="h1">
              {item.elements.title.value}
            </OldHeading>
            <OldText>{item.elements.description.value}</OldText>
          </Box>
          <FeaturedItem {...featuredItem} />
          <Grid
            gridTemplateColumns={{ _: '1fr', medium: 'repeat(6, 1fr)' }}
            gridRowGap={{ _: '2.5rem', medium: '2rem', large: '2.5rem' }}
            gridColumnGap={{ _: '1.5rem', large: '2rem' }}
            mt={{ _: '2.5rem', medium: '2rem', large: '2.5rem' }}
          >
            {status === 'loading'
              ? [...Array.from(new Array(8))].map((_, key) => (
                  <NewsItem
                    status="loading"
                    variant={[3, 4].includes(key) ? 'wide' : 'regular'}
                    key={key}
                  />
                ))
              : articles &&
                articles.items.length > 0 &&
                articles.items.map((item, key) => (
                  <NewsItem
                    status="idle"
                    variant={[3, 4].includes(key) ? 'wide' : 'regular'}
                    key={key}
                    {...item}
                  />
                ))}
          </Grid>
          {articles && articles.items && articles.pagination.total_count > 8 && (
            <Pagination
              setPage={(page) => setPage(page)}
              totalCount={articles.pagination.total_count || 0}
              limit={8}
              currentPage={page}
            />
          )}
        </Container>
        <Footer />
      </PageTransition>
    </Page>
  )
}

export default NewsOverviewPage

export const getStaticProps: GetStaticProps = async ({ locale, preview }) => {
  const content = await getPageByCodename<NewsOverviewPageModel>({
    codename: 'news_page',
    lang: locale,
    preview,
  })
  return {
    props: {
      ...content,
      ...(await getServerSideTranslations(locale)),
    },
  }
}
